import React, { useEffect, useState } from "react";
import { api } from "../../api/client.js";
import { demoAssets } from "../shared/constants.js";

type AuthConfig = {
  mode: "local" | "company";
  capabilities: Record<string, boolean>;
  legal?: { termsUrl: string; privacyUrl: string };
};

function LocalAuthScreen({ onAuthed }: { onAuthed: () => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const switchMode = (nextMode: "login" | "register") => {
    setMode(nextMode);
    setError("");
  };
  const authErrorMessage = (message: string) => ({
    "invalid invite code": "邀请码不正确",
    "password confirmation does not match": "两次输入的密码不一致",
    "invalid username or password": "用户名需为 3-40 位字母、数字或符号，密码至少 8 位",
    "username already exists": "用户名已存在",
    "invalid credentials": "用户名或密码不正确"
  })[message] || message;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (mode === "register" && password !== passwordConfirm) {
      setError("两次输入的密码不一致");
      return;
    }
    try {
      await api(mode === "login" ? "/api/auth/login" : "/api/auth/register", {
        method: "POST",
        body: JSON.stringify(
          mode === "login"
            ? { username, password }
            : { username, password, passwordConfirm, displayName, inviteCode }
        )
      });
      onAuthed();
    } catch (err) {
      setError(err instanceof Error ? authErrorMessage(err.message) : "登录失败");
    }
  };
  return (
    <main className="auth-shell">
      <form className="auth-panel" onSubmit={submit}>
        <div className="auth-heading">
          <img className="auth-logo" src={demoAssets.mark} alt="" />
          <div>
            <h1>Truth Seeker 科研助理</h1>
            <p>登录后进入科研问答工作台。</p>
          </div>
        </div>
        <div className="auth-tabs">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")}>登录</button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")}>注册</button>
        </div>
        <label>
          用户名
          <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
        </label>
        {mode === "register" && (
          <label>
            显示名
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" />
          </label>
        )}
        {mode === "register" && (
          <label>
            邀请码
            <input value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} autoComplete="off" />
          </label>
        )}
        <label>
          密码
          <input value={password} type="password" onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} />
        </label>
        {mode === "register" && (
          <label>
            确认密码
            <input value={passwordConfirm} type="password" onChange={(event) => setPasswordConfirm(event.target.value)} autoComplete="new-password" />
          </label>
        )}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="primary-action" type="submit">{mode === "login" ? "进入工作台" : "创建账号"}</button>
      </form>
    </main>
  );
}

type CompanyPage = "login" | "register" | "reset";
type LoginMethod = "phone_code" | "phone_password" | "email_password";

function CodeField({
  value,
  onChange,
  onSend,
  cooldown,
  busy
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  cooldown: number;
  busy: boolean;
}) {
  return (
    <label>
      验证码
      <span className="auth-code-row">
        <input
          aria-label="验证码"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="请输入验证码"
        />
        <button type="button" className="auth-code-button" disabled={busy || cooldown > 0} onClick={onSend}>
          {busy ? "发送中…" : cooldown > 0 ? `${cooldown} 秒` : "获取验证码"}
        </button>
      </span>
    </label>
  );
}

function PasswordField({ label = "密码", value, onChange, autoComplete = "current-password" }: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label>
      {label}
      <span className="auth-password-row">
        <input
          aria-label={label}
          value={value}
          type={visible ? "text" : "password"}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          placeholder={`请输入${label}`}
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={visible ? `隐藏${label}` : `显示${label}`}>
          {visible ? "隐藏" : "显示"}
        </button>
      </span>
    </label>
  );
}

function CompanyAuthScreen({ config, onAuthed }: { config: AuthConfig; onAuthed: () => void }) {
  const [page, setPage] = useState<CompanyPage>("login");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("phone_code");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [workUnit, setWorkUnit] = useState("");
  const [job, setJob] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown > 0]);

  const changePage = (next: CompanyPage) => {
    setPage(next);
    setError("");
    setNotice("");
    setCode("");
    setPassword("");
    setCooldown(0);
  };

  const sendCode = async () => {
    setError("");
    if (!/^1\d{10}$/.test(phone.trim())) {
      setError("请输入 11 位有效手机号");
      return;
    }
    setSending(true);
    try {
      const result = await api<{ cooldownTime: number }>("/api/auth/send-code", {
        method: "POST",
        body: JSON.stringify({ phoneNumber: phone.trim(), purpose: page === "register" ? "register" : page === "reset" ? "resetPassword" : "login" })
      });
      setCooldown(result.cooldownTime || 60);
      setNotice("验证码已发送，请注意查收。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "验证码发送失败，请稍后重试");
    } finally {
      setSending(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      if (page === "login") {
        const payload = loginMethod === "phone_code"
          ? { method: loginMethod, phone: phone.trim(), verificationCode: code.trim() }
          : loginMethod === "phone_password"
            ? { method: loginMethod, phone: phone.trim(), password }
            : { method: loginMethod, email: email.trim(), password };
        await api("/api/auth/login", { method: "POST", body: JSON.stringify(payload) });
        onAuthed();
      } else if (page === "register") {
        await api("/api/auth/register", {
          method: "POST",
          body: JSON.stringify({ phone: phone.trim(), verificationCode: code.trim(), name: name.trim(), email: email.trim(), workUnit: workUnit.trim(), job: job.trim(), agreeTerms })
        });
        onAuthed();
      } else {
        await api("/api/auth/reset-password", {
          method: "POST",
          body: JSON.stringify({ phoneNumber: phone.trim(), code: code.trim(), newPassword: password })
        });
        setNotice("密码重置成功，请使用新密码登录。");
        window.setTimeout(() => changePage("login"), 1000);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-shell company-auth-shell">
      <form className={`auth-panel company-auth-panel ${page === "register" ? "company-auth-panel-wide" : ""}`} onSubmit={submit}>
        <header className="company-auth-heading">
          <img className="auth-logo" src={demoAssets.mark} alt="" />
          <h1>TruthSeeker</h1>
          <p>{page === "login" ? "欢迎回来" : page === "register" ? "创建您的账户" : "重置登录密码"}</p>
        </header>

        {page === "login" && (
          <>
            <div className="company-login-methods" role="tablist" aria-label="登录方式">
              <button type="button" role="tab" aria-selected={loginMethod === "phone_code"} className={loginMethod === "phone_code" ? "active" : ""} onClick={() => setLoginMethod("phone_code")}>验证码登录</button>
              <button type="button" role="tab" aria-selected={loginMethod === "phone_password"} className={loginMethod === "phone_password" ? "active" : ""} onClick={() => setLoginMethod("phone_password")}>手机密码</button>
              <button type="button" role="tab" aria-selected={loginMethod === "email_password"} className={loginMethod === "email_password" ? "active" : ""} onClick={() => setLoginMethod("email_password")}>邮箱密码</button>
            </div>
            {loginMethod === "email_password" ? (
              <label>邮箱地址<input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="请输入邮箱地址" /></label>
            ) : (
              <label>手机号<input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="请输入手机号" /></label>
            )}
            {loginMethod === "phone_code"
              ? <CodeField value={code} onChange={setCode} onSend={sendCode} cooldown={cooldown} busy={sending} />
              : <PasswordField value={password} onChange={setPassword} />}
          </>
        )}

        {page === "register" && (
          <>
            <label>手机号<input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="请输入手机号" /></label>
            <CodeField value={code} onChange={setCode} onSend={sendCode} cooldown={cooldown} busy={sending} />
            <label>用户名<input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" placeholder="请输入用户名" /></label>
            <label>邮箱地址<input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="请输入邮箱地址" /></label>
            <label>工作单位（可选）<input value={workUnit} onChange={(event) => setWorkUnit(event.target.value)} autoComplete="organization" placeholder="请输入单位" /></label>
            <label>职位（可选）<input value={job} onChange={(event) => setJob(event.target.value)} autoComplete="organization-title" placeholder="请输入职位" /></label>
            <label className="auth-agreement">
              <input type="checkbox" checked={agreeTerms} onChange={(event) => setAgreeTerms(event.target.checked)} />
              <span>我已阅读并同意 <a href={config.legal?.termsUrl || "/legal/terms"} target="_blank" rel="noreferrer">《用户协议》</a> 和 <a href={config.legal?.privacyUrl || "/legal/privacy"} target="_blank" rel="noreferrer">《隐私政策》</a></span>
            </label>
          </>
        )}

        {page === "reset" && (
          <>
            <label>手机号<input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="请输入手机号" /></label>
            <CodeField value={code} onChange={setCode} onSend={sendCode} cooldown={cooldown} busy={sending} />
            <PasswordField label="新密码" value={password} onChange={setPassword} autoComplete="new-password" />
          </>
        )}

        {error && <p className="auth-error" role="alert">{error}</p>}
        {notice && <p className="auth-notice" role="status">{notice}</p>}
        <button className="primary-action" type="submit" disabled={submitting}>
          {submitting ? "处理中…" : page === "login" ? "登录" : page === "register" ? "注册" : "重置密码"}
        </button>
        <footer className="company-auth-footer">
          {page === "login" ? (
            <>
              <button type="button" onClick={() => changePage("reset")}>忘记密码？</button>
              <span>还没有账户？ <button type="button" onClick={() => changePage("register")}>立即注册</button></span>
            </>
          ) : (
            <span>已有账户？ <button type="button" onClick={() => changePage("login")}>立即登录</button></span>
          )}
        </footer>
      </form>
    </main>
  );
}

export function AuthScreen({ onAuthed }: { onAuthed: () => void }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api<AuthConfig>("/api/auth/config")
      .then((value) => active && setConfig(value))
      .catch(() => active && setError("认证服务配置暂时不可用，请稍后刷新。"));
    return () => { active = false; };
  }, []);

  if (error) return <main className="boot-screen" role="alert">{error}</main>;
  if (!config) return <main className="boot-screen">Truth Seeker</main>;
  return config.mode === "company"
    ? <CompanyAuthScreen config={config} onAuthed={onAuthed} />
    : <LocalAuthScreen onAuthed={onAuthed} />;
}
